import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { questionsApi } from '../lib/questions';
import { resultsApi } from '../lib/results';
import PaywallModal from '../components/PaywallModal';
import {
  ArrowLeft,
  Check,
  X,
  Clock,
  Trophy,
  RotateCcw,
  Home,
  ChevronRight,
  Heart,
  Lock
} from 'lucide-react';

const FREE_QUESTIONS_LIMIT = 10; // Počet otázek zdarma pro každou kategorii

const SUPABASE_URL = 'https://ffccmyllnijevswabqhr.supabase.co';

// Helper pro URL obrázku
const getImageUrl = (imageName) => {
  if (!imageName) return null;
  // PNG: začíná na 'i' nebo '7' nebo '9' a obsahuje velká písmena (7B, 7CH, 9CH)
  const isPng = imageName.startsWith('i') ||
                imageName.startsWith('7B') ||
                imageName.startsWith('7CH') ||
                imageName.startsWith('9CH');
  const ext = isPng ? 'png' : 'jpg';
  return `${SUPABASE_URL}/storage/v1/object/public/question-images/${imageName}.${ext}`;
};

const Quiz = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user, subscription, loading: authLoading } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]); // Všechny otázky pro zobrazení progress
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizFinished, setQuizFinished] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [resultSaved, setResultSaved] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const hasActiveSubscription = subscription?.hasSubscription;

  // Ochrana proti kopírování - zakázat pravé tlačítko a výběr textu
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      // Zakázat Ctrl+C, Ctrl+A, Ctrl+P (print)
      if (e.ctrlKey && (e.key === 'c' || e.key === 'a' || e.key === 'p' || e.key === 'C' || e.key === 'A' || e.key === 'P')) {
        e.preventDefault();
        return false;
      }
      // Zakázat Cmd+C, Cmd+A, Cmd+P pro Mac
      if (e.metaKey && (e.key === 'c' || e.key === 'a' || e.key === 'p' || e.key === 'C' || e.key === 'A' || e.key === 'P')) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Přesměrovat nepřihlášeného uživatele
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { redirectTo: `/quiz/${category}` } });
    }
  }, [authLoading, user, navigate, category]);

  useEffect(() => {
    if (user) {
      loadQuestions();
      loadFavorites();
    }
  }, [category, user]);

  const loadFavorites = async () => {
    if (!user) return;
    try {
      const ids = await resultsApi.getFavoriteIds(user.id);
      setFavoriteIds(ids);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  useEffect(() => {
    if (!quizFinished && !loading) {
      const timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quizFinished, loading]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      // Načíst všechny otázky seřazené podle question_number
      const data = await questionsApi.getQuestionsSorted(category);
      setAllQuestions(data);

      // Pro free uživatele omezit na prvních 10 otázek
      if (hasActiveSubscription) {
        setQuestions(data);
      } else {
        setQuestions(data.slice(0, FREE_QUESTIONS_LIMIT));
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(index);
    const isCorrect = index === questions[currentIndex].correct_answer_index;

    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    // Uložit odpověď pod indexem otázky pro snadný přístup při navigaci
    setAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[currentIndex] = {
        questionId: questions[currentIndex].id,
        selected: index,
        correct: questions[currentIndex].correct_answer_index,
        isCorrect
      };
      return newAnswers;
    });

    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizFinished(true);
    }
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setAnswers([]);
    setQuizFinished(false);
    setTimeElapsed(0);
    loadQuestions();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Uložit výsledek do databáze
  const saveQuizResult = useCallback(async () => {
    if (resultSaved || !user || questions.length === 0) return;

    try {
      const percentage = Math.round((score / questions.length) * 100);

      // Vypočítat celkové body (totalScore a maxScore)
      let totalScore = 0;
      let maxScore = 0;

      answers.forEach((a, idx) => {
        const questionScoreValue = questions[idx]?.scorevalue || questions[idx]?.score_value || 1;
        maxScore += questionScoreValue;
        if (a.isCorrect) {
          totalScore += questionScoreValue;
        }
      });

      await resultsApi.saveResult(user.id, {
        type: 'quiz',
        category: category,
        score: score,
        totalQuestions: questions.length,
        percentage: percentage,
        timeSeconds: timeElapsed,
        totalScore: totalScore,
        maxScore: maxScore,
        answers: answers.map((a, idx) => ({
          questionId: a.questionId,
          questionText: questions[idx]?.question_text,
          options: questions[idx]?.options,
          imageName: questions[idx]?.image_name,
          scoreValue: questions[idx]?.scorevalue || questions[idx]?.score_value || 1,
          selected: a.selected,
          correct: a.correct,
          isCorrect: a.isCorrect
        }))
      });
      setResultSaved(true);
      console.log('Quiz result saved successfully');
    } catch (error) {
      console.error('Error saving result:', error);
    }
  }, [resultSaved, user, questions, score, category, timeElapsed, answers]);

  // Toggle oblíbené
  const toggleFavorite = async (questionId) => {
    if (!user) return;

    try {
      if (favoriteIds.has(questionId)) {
        await resultsApi.removeFavorite(user.id, questionId);
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(questionId);
          return newSet;
        });
      } else {
        await resultsApi.addFavorite(user.id, questionId);
        setFavoriteIds(prev => new Set([...prev, questionId]));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Uložit výsledek když je kvíz dokončen
  useEffect(() => {
    if (quizFinished && !resultSaved && user) {
      saveQuizResult();
    }
  }, [quizFinished, resultSaved, user, saveQuizResult]);

  // === PODMÍNĚNÉ RETURNY (musí být až po všech hooks) ===

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 flex items-center justify-center">
        <div className="text-white">Načítám otázky...</div>
      </div>
    );
  }

  if (quizFinished) {
    const percentage = Math.round((score / questions.length) * 100);
    const isFreeTrial = !hasActiveSubscription;

    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-6">
              <Trophy size={40} className="text-white" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">Kvíz dokončen!</h1>
            <p className="text-dark-400 mb-8">{category}</p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-primary-400">{score}/{questions.length}</p>
                <p className="text-dark-400 text-sm">Správně</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{percentage}%</p>
                <p className="text-dark-400 text-sm">Úspěšnost</p>
              </div>
              <div className="glass rounded-xl p-4">
                <p className="text-3xl font-bold text-white">{formatTime(timeElapsed)}</p>
                <p className="text-dark-400 text-sm">Čas</p>
              </div>
            </div>

            {/* Upsell pro free uživatele */}
            {isFreeTrial && (
              <div className="glass rounded-xl p-6 mb-6 border border-primary-500/30">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Lock size={20} className="text-primary-400" />
                  <h3 className="text-lg font-semibold text-white">Vyzkoušel jsi {FREE_QUESTIONS_LIMIT} otázek zdarma</h3>
                </div>
                <p className="text-dark-400 mb-4">
                  V kategorii {category} je celkem {allQuestions.length} otázek.
                  Odemkni Premium a získej přístup ke všem!
                </p>
                <button
                  onClick={() => setShowPaywall(true)}
                  className="px-6 py-3 rounded-xl gradient-bg text-white font-semibold hover:opacity-90 transition-opacity"
                >
                  Odemknout všechny otázky
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleRestart}
                className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors flex items-center justify-center space-x-2"
              >
                <RotateCcw size={20} />
                <span>Zkusit znovu</span>
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 rounded-xl bg-dark-700 text-white font-semibold hover:bg-dark-600 transition-colors flex items-center justify-center space-x-2"
              >
                <Home size={20} />
                <span>Dashboard</span>
              </button>
            </div>
          </div>
        </div>

        {/* Paywall Modal */}
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          title="Odemkni všechny otázky"
          message={`Získej přístup ke všem ${allQuestions.length} otázkám z kategorie ${category}`}
        />
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-dark-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Zpět</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-dark-400">
              <Clock size={18} />
              <span>{formatTime(timeElapsed)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max={questions.length}
                value={currentIndex + 1}
                onChange={(e) => {
                  const num = parseInt(e.target.value);
                  if (num >= 1 && num <= questions.length) {
                    const newIndex = num - 1;
                    setCurrentIndex(newIndex);
                    setSelectedAnswer(answers[newIndex]?.selected ?? null);
                    setShowExplanation(answers[newIndex] !== undefined);
                  }
                }}
                className="w-16 px-2 py-1 rounded-lg bg-dark-800 text-white text-center border border-dark-700 focus:border-primary-500 focus:outline-none"
              />
              <span className="text-white font-medium">/ {questions.length}</span>
            </div>
          </div>
        </div>

        {/* Progress bar - clickable slider */}
        <div className="mb-8">
          <input
            type="range"
            min="0"
            max={questions.length - 1}
            value={currentIndex}
            onChange={(e) => {
              const newIndex = parseInt(e.target.value);
              setCurrentIndex(newIndex);
              setSelectedAnswer(answers[newIndex]?.selected ?? null);
              setShowExplanation(answers[newIndex] !== undefined);
            }}
            className="w-full h-2 bg-dark-800 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-primary-500
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-moz-range-thumb]:w-4
              [&::-moz-range-thumb]:h-4
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-primary-500
              [&::-moz-range-thumb]:cursor-pointer
              [&::-moz-range-thumb]:border-0"
            style={{
              background: `linear-gradient(to right, rgb(139, 92, 246) 0%, rgb(168, 85, 247) ${((currentIndex + 1) / questions.length) * 100}%, rgb(31, 41, 55) ${((currentIndex + 1) / questions.length) * 100}%, rgb(31, 41, 55) 100%)`
            }}
          />
          <div className="flex justify-between text-dark-400 text-xs mt-1">
            <span>Otázka 1</span>
            <span>Otázka {questions.length}</span>
          </div>
        </div>

        {/* Question - s ochranou proti kopírování */}
        <div
          className="glass rounded-2xl p-6 mb-6"
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
          onDragStart={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-sm font-medium">
                {category}
              </span>
              <span className="px-3 py-1 rounded-full bg-dark-700 text-dark-300 text-sm font-medium">
                Otázka #{currentQuestion.question_number}
              </span>
            </div>
            {/* Tlačítko oblíbené */}
            <button
              onClick={() => toggleFavorite(currentQuestion.id)}
              className={`p-2 rounded-lg transition-colors ${
                favoriteIds.has(currentQuestion.id)
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-dark-700 text-dark-400 hover:text-red-400'
              }`}
              title={favoriteIds.has(currentQuestion.id) ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
            >
              <Heart
                size={20}
                fill={favoriteIds.has(currentQuestion.id) ? 'currentColor' : 'none'}
              />
            </button>
          </div>

          <h2 className="text-xl font-semibold text-white mb-6 whitespace-pre-line">
            {currentQuestion.question_text}
          </h2>

          {/* Question Image */}
          {currentQuestion.image_name && (
            <div className="mb-6">
              <img
                src={getImageUrl(currentQuestion.image_name)}
                alt="Obrázek k otázce"
                className="max-w-full h-auto rounded-xl border border-dark-700 mx-auto"
                style={{ maxHeight: '300px' }}
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === currentQuestion.correct_answer_index;
              const showResult = selectedAnswer !== null;

              let buttonClass = 'glass';
              if (showResult) {
                if (isCorrect) {
                  buttonClass = 'bg-green-500/20 border border-green-500';
                } else if (isSelected && !isCorrect) {
                  buttonClass = 'bg-red-500/20 border border-red-500';
                }
              } else if (isSelected) {
                buttonClass = 'bg-primary-500/20 border border-primary-500';
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={selectedAnswer !== null}
                  className={`w-full p-4 rounded-xl text-left transition-all ${buttonClass} ${
                    selectedAnswer === null ? 'hover:bg-dark-700' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white">{option}</span>
                    {showResult && isCorrect && (
                      <Check size={20} className="text-green-400" />
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <X size={20} className="text-red-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Explanation - s ochranou proti kopírování */}
        {showExplanation && currentQuestion.explanation && (
          <div
            className="glass rounded-2xl p-6 mb-6 border border-primary-500/30"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            onDragStart={(e) => e.preventDefault()}
          >
            <h3 className="text-lg font-semibold text-white mb-2">Vysvětlení</h3>
            <p className="text-dark-300">{currentQuestion.explanation}</p>
          </div>
        )}

        {/* Next button */}
        {selectedAnswer !== null && (
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors flex items-center space-x-2"
            >
              <span>{currentIndex < questions.length - 1 ? 'Další otázka' : 'Dokončit'}</span>
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
